// src/features/tag/components/RenameTagModal.tsx

import { useEffect } from 'react';
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TagManageUtils } from '../TagManageUtils';
import { TAG_MANAGE } from '@/lib/constants/tag';
import type { TagUsage } from '@/lib/types/Tag';

interface RenameTagModalProps {
  usage: TagUsage | null;
  usages: TagUsage[];
  isPending: boolean;
  onClose: () => void;
  onRename: (tagId: number, label: string) => void;
}

interface RenameFormValues {
  label: string;
}

/**
 * Rename keeps the tag's identity, so smart playlists that use it keep
 * matching the same content — only the displayed name changes everywhere.
 * Renaming to another existing tag's name is rejected (that would be a merge).
 */
export function RenameTagModal({ usage, usages, isPending, onClose, onRename }: RenameTagModalProps) {
  const schema = z
    .object({ label: z.string() })
    .superRefine((values, ctx) => {
      if (!usage) return;
      const error = TagManageUtils.validateRenameLabel(values.label, usages, usage.tagId);
      if (error) ctx.addIssue({ code: 'custom', message: error, path: ['label'] });
    });

  const { control, handleSubmit, reset } = useForm<RenameFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { label: usage?.label ?? '' },
  });
  const labelField = useController({ name: 'label', control });

  // Re-seed the field each time a different tag is opened for rename.
  useEffect(() => {
    reset({ label: usage?.label ?? '' });
  }, [usage, reset]);

  const submit = handleSubmit((values) => {
    if (!usage) return;
    const label = values.label.trim();
    if (label === usage.label) {
      onClose();
      return;
    }
    onRename(usage.tagId, label);
  });

  return (
    <Modal opened={usage !== null} onClose={onClose} title="Rename Tag" size="md">
      <form onSubmit={submit}>
        <Stack gap="md">
          <TextInput
            label="Tag name"
            data-autofocus
            maxLength={TAG_MANAGE.LABEL_MAX_LENGTH}
            value={labelField.field.value}
            onChange={labelField.field.onChange}
            onBlur={labelField.field.onBlur}
            error={labelField.fieldState.error?.message}
          />
          <Text size="sm" c="dimmed">
            The tag is renamed everywhere it appears — on media and in Smart
            Playlist rules. Playlists keep matching the same content.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              Rename Tag
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
